import { XStack, YStack } from '@/components/ui/layout/layout';
import { Separator } from '@/components/ui/Separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { cn } from '@/lib/utils';

import { REFERRAL_CREDIT } from './referral.constants';
import {
  type ReferralStatsResponse,
  type ReferredUser,
} from './referral.types';

interface StatusTabProps {
  stats: ReferralStatsResponse | undefined;
  isLoading: boolean;
}

export function StatusTab({ stats, isLoading }: StatusTabProps) {
  if (isLoading) {
    return (
      <YStack className="gap-4 p-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </YStack>
    );
  }

  const users = stats?.referred_users ?? [];
  const isActivity = stats?.referrer_reward_type === 'activity_based';

  return (
    <YStack className="gap-4">
      {stats && (
        <XStack className="gap-4 p-4 bg-surface-hover rounded-2xl border border-border">
          <YStack className="flex-1 gap-1">
            <span className="text-sm text-foreground-muted">
              Total credits earned
            </span>
            <span className="text-lg font-semibold text-success-11">
              ${(stats.total_credits_earned_cents / 100).toFixed(2)}
            </span>
          </YStack>
          <YStack className="flex-1 gap-1">
            <span className="text-sm text-foreground-muted">Successful</span>
            <span className="text-lg font-semibold">
              {users.filter((u) => u.status === 'successful').length}
            </span>
          </YStack>
        </XStack>
      )}

      {users.length === 0 ? (
        <YStack className="py-8 items-center justify-center">
          <p className="text-sm text-foreground-muted">
            No referrals yet. Share your code to get started!
          </p>
        </YStack>
      ) : (
        <div className="overflow-y-auto rounded-lg border border-border">
          <Table>
            <TableHeader className="sticky top-0 bg-surface">
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user, i) => (
                <ReferralRow key={user.email + i} user={user} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <YStack className="gap-4">
        <Separator />
        <YStack className="gap-1">
          <span className="text-sm font-medium text-foreground">
            How do referrals work?
          </span>
          <span className="text-sm text-foreground-muted">
            {isActivity
              ? `When your friend signs up with your code and uses LittleBird for 3 days, you'll receive 1 free month (${REFERRAL_CREDIT.ACTIVITY} credit).`
              : `When your friend signs up using your referral code and makes their first payment, you'll receive 2 free months (${REFERRAL_CREDIT.PAYMENT} credit).`}
          </span>
        </YStack>
        <YStack className="gap-1">
          <span className="text-sm font-medium text-foreground">
            Is there a limit to how many friends I can refer?
          </span>
          <span className="text-sm text-foreground-muted">
            {isActivity
              ? `No, there's no limit! Refer as many friends as you like and earn 1 free month (${REFERRAL_CREDIT.ACTIVITY} credit) for each one who uses LittleBird for 3 days.`
              : `No, there's no limit! You can refer as many friends as you like and earn 2 free months (${REFERRAL_CREDIT.PAYMENT} credit) for each successful referral.`}
          </span>
        </YStack>
        <YStack className="gap-1">
          <span className="text-sm font-medium text-foreground">
            When will I receive my credit?
          </span>
          <span className="text-sm text-foreground-muted">
            {isActivity
              ? `Your ${REFERRAL_CREDIT.ACTIVITY} credit will be automatically applied to your account after your friend uses LittleBird for 3 days.`
              : `Your ${REFERRAL_CREDIT.PAYMENT} credit will be automatically applied to your account as soon as your friend makes their first payment.`}
          </span>
        </YStack>
      </YStack>
    </YStack>
  );
}

function ReferralRow({ user }: { user: ReferredUser }) {
  const isPending = user.status === 'pending';
  const isActivity = user.reward_type === 'activity_based';

  const badgeLabel = (() => {
    if (!isPending) return isActivity ? 'Reward earned!' : 'Successful';
    if (!isActivity) return 'Pending';
    const days = user.qualifying_days_completed ?? 0;
    if (days === 0) return 'Signed up';
    return `Day ${Math.min(days, 3)} of 3`;
  })();

  return (
    <TableRow>
      <TableCell>
        <YStack className="gap-0.5">
          {user.name && <span className="font-medium">{user.name}</span>}
          <span className={cn('text-sm', user.name && 'text-foreground-muted')}>
            {user.email}
          </span>
        </YStack>
      </TableCell>
      <TableCell>
        <span
          className={cn(
            'px-2 py-1 rounded-md text-sm font-medium',
            isPending
              ? 'bg-secondary text-foreground-muted'
              : 'bg-success-9 text-success-1'
          )}
        >
          {badgeLabel}
        </span>
      </TableCell>
      <TableCell className="text-sm text-foreground-muted">
        {new Date(user.referred_at).toLocaleDateString()}
      </TableCell>
    </TableRow>
  );
}
